import { PLAYER_SHADES } from "@/lib/api";
import Building from "@/components/game/Building";
import BoardMarkers from "@/components/game/BoardMarkers";
import ThiefBanner from "@/components/game/ThiefBanner";
import WeatherArea from "@/components/game/WeatherArea";
import SeaSquare, { WallSplash, drownedText } from "@/components/game/SeaSquare";
import StreetTile from "@/components/game/StreetTile";
import { TileImage } from "@/components/game/TileImage";
import { SideFaces, FaceShadows } from "@/components/game/TerrainBlock";
import { FACE, TOP_EDGE, LAYER, boardGeom, heightLabel, heightOf } from "@/lib/terrain";
import EdgeWalls from "@/components/game/EdgeWalls";
import { useEffect, useMemo, useRef, useState } from "react";

// 3D BOARD: squares touch (no gap); the tray padding lives in BoardArea.
export const BOARD_GAP = 0;
export const BOARD_PAD = 0;

// Insets (px) from the top face's edges to the part of it no later block covers.
const visInset = (v, extra = 0) => ({ left: extra, top: extra, right: v.oR + extra, bottom: v.oB + extra });

// TERRAIN 2 of 3: dry squares at or below the sea level that stay dry only because walls block
// every way in (server flood_view.held_back). Diagonal hatch on the top face.
function HeldBackOverlay({ cellKey, inset }) {
  return (
    <span
      data-testid={`held-back-${cellKey}`}
      title="Held back by Sea Walls: the sea would be here without them."
      className="absolute pointer-events-none"
      style={{
        ...inset,
        zIndex: 9,
        background: "repeating-linear-gradient(135deg, rgba(36,73,90,0.28) 0 2px, transparent 2px 9px)",
        boxShadow: "inset 0 0 0 1px rgba(36,73,90,0.4)",
      }}
    />
  );
}

// Squares with a building that WILL flood at the next sea rise with the walls as they are now
// (server flood_view.next_rise; empty squares never). 2 px dashed, 2 px inside the top face.
function NextRiseOutline({ cellKey, inset, plot = false }) {
  if (plot) {
    // Building squares: around the plot (15% in, inside the sidewalk), thicker, on a pale backing
    // so it cannot read as road paint.
    return (
      <span
        data-testid={`next-rise-${cellKey}`}
        title="Floods at the next sea rise (with the walls as they are now)"
        className="absolute pointer-events-none"
        style={{ left: "15%", top: "15%", right: "15%", bottom: "15%", zIndex: 10, border: "4px dashed #24495A", background: "rgba(251,250,247,0.35)", boxShadow: "0 0 0 2px rgba(251,250,247,0.85)", borderRadius: 2 }}
      />
    );
  }
  return (
    <span
      data-testid={`next-rise-${cellKey}`}
      title="Floods at the next sea rise (with the walls as they are now)"
      className="absolute pointer-events-none"
      style={{ ...inset, zIndex: 10, border: "2px dashed #24495A", borderRadius: 2 }}
    />
  );
}

function SabotageMark({ cellKey }) {
  return (
    <div data-testid={`sabotage-marker-${cellKey}`} className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ zIndex: 10 }} title="Sabotaged — repair with Repair Card">
      <svg viewBox="0 0 40 40" width="42%" height="42%" aria-hidden="true">
        <circle cx="20" cy="20" r="16" fill="var(--danger)" stroke="#fff" strokeWidth="2" />
        <path d="M13 13 L27 27 M27 13 L13 27" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function DemolishMark({ r, c }) {
  return (
    <svg data-testid={`demolish-x-${r}-${c}`} className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }} viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">
      <path d="M12 12 L28 28 M28 12 L12 28" stroke="var(--danger)" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

const FALLBACK_SHADE = { roof: "#B9B3A6", wall: "#8A8C85", edge: "#5F625C" };
const hasGreenStreet = (cell) => (cell?.edge_tokens || []).some((t) => t?.card_key === "green_street");
const floorsOf = (cell) => (cell?.kind === "cube" ? (cell.cube_stack || []).length : 0);

// TERRAIN 3 of 3: a building is at most 3 floors high on every square — the same rule as the
// backend (board_mechanics.cube_rules.max_floors).
export const DEFAULT_MAX_FLOORS = 3;

// Starting cube (Phase 1): an empty square, or on top of your own cubes below the floor limit.
const setupLegal = (cell, color, cap) => {
  if (!cell?.kind) return true;
  if (cell.kind !== "cube" || (cell.owner && cell.owner !== color)) return false;
  const stack = cell.cube_stack || [];
  return stack.every((e) => !e?.owner || e.owner === color) && stack.length < cap;
};

export default function BoardGrid({
  gridCols,
  gridRows,
  cells,
  // TERRAIN 1 of 3: {"r,c": 1..4} (fixed for the game) -> the block's height, colours and "▲2" label.
  terrain = null,
  players = [],
  selectedCell,
  onSelectCell,
  phase,
  mode,
  selectedCard,
  activeColor,
  startingCubeMode = false,
  legalCells = null,
  actionTargets = null,
  actionChosen = [],
  demolishMode = false,
  weather = null,
  weatherShuffle = null,
  thiefBanner = null,
  round = 0,
  // TERRAIN 2 of 3: {sea_level, coastal, held_back, next_rise} (server) + the edge walls.
  floodView = null,
  walls = {},
  // {cell, options: [{side, ok, reason}], onPick(side)} while an edge must be chosen.
  edgePicker = null,
  onHoverCell = null,
  illegalCell = null,
  fallingCell = null,
  seaRise = {},
  wallSplash = {},
  maxFloors = DEFAULT_MAX_FLOORS,
  cellPx,
  // Tile reading: only while nothing else waits for a board click (no build card selected,
  // no action-card target, no demolish, no starting cube to place). Click an occupied square
  // -> onReadCell(key); click an empty square -> onReadCell(null).
  readMode = false,
  readCell = null,
  onReadCell = null,
}) {
  const [hovered, setHovered] = useState(null);
  // Floors seen on the previous render, so a new storey can slide up.
  const seen = useRef(null);
  useEffect(() => {
    seen.current = Object.fromEntries(Object.entries(cells).map(([k, v]) => [k, floorsOf(v)]));
  });
  const riseFrom = (key, cell) => (seen.current ? seen.current[key] ?? 0 : floorsOf(cell));
  const S = cellPx;
  const geom = useMemo(() => boardGeom(terrain, gridRows, gridCols, S), [terrain, gridRows, gridCols, S]);
  const placingFloor = startingCubeMode || !!selectedCard?.placement?.is_cube;
  const coastal = new Set(floodView?.coastal || []);
  const heldBack = new Set(floodView?.held_back || []);
  const nextRise = new Set(floodView?.next_rise || []);
  const cap = Number.isFinite(Number(maxFloors)) && Number(maxFloors) > 0 ? Number(maxFloors) : DEFAULT_MAX_FLOORS;
  const canBuildNow = phase === "action" && (mode === "setup" || mode === "main");
  const inFlow = !!actionTargets || demolishMode;
  // Legality: server build preview for Build cards; the starting-cube rule during setup.
  const showLegality = !inFlow && canBuildNow && (startingCubeMode || selectedCard?.kind === "build");

  const isLegal = (key, cell) => {
    if (!showLegality) return false;
    if (startingCubeMode) return setupLegal(cell, activeColor, cap);
    return !!legalCells?.has(key);
  };

  const cellInfo = (r, c) => {
    const key = `${r},${c}`;
    const cell = cells[key];
    const legal = isLegal(key, cell);
    const isDemolishTarget = demolishMode && !!cell?.kind && cell.owner === activeColor && cell.kind !== "sea_marker";
    const isActionTarget = !!actionTargets?.includes?.(key);
    const clickable = demolishMode ? isDemolishTarget : legal || isActionTarget;
    // Reading never takes over a click that already means something.
    const readable = !!onReadCell && readMode && !inFlow && !clickable;
    const dimmed = showLegality && !legal;
    const height = heightOf(terrain, key);
    const flooded = cell?.kind === "sea_marker";
    return { key, cell, legal, isDemolishTarget, isActionTarget, clickable, readable, dimmed, height, flooded };
  };

  // Painter's order: row by row from the top, left to right in each row.
  const order = [];
  for (let r = 0; r < gridRows; r += 1) for (let c = 0; c < gridCols; c += 1) order.push([r, c]);

  return (
    <div
      data-testid="board-grid"
      data-square={S}
      className="relative"
      onMouseLeave={() => {
        setHovered(null);
        onHoverCell?.(null);
      }}
      style={{ width: geom.width, height: geom.height }}
    >
      {order.map(([r, c]) => {
        const { key, cell, legal, isDemolishTarget, isActionTarget, clickable, readable, dimmed, height, flooded } = cellInfo(r, c);
        const lv = height ?? 1;
        const f = geom.face(key);
        const v = geom.vis(key);
        const floors = floorsOf(cell);
        const falling = fallingCell === key;
        const showGhost = legal && placingFloor && floors < cap && (hovered === key || selectedCell === key);
        const isCube = cell?.kind === "cube";
        const isTile = cell?.kind === "tile";
        const isSea = cell?.kind === "sea_marker";
        const drowned = isSea ? cell.drowned : null;
        const readsCard = readable && (isTile || isCube || !!drowned);
        const seaLabel = drowned ? drownedText(drowned, players) : `Sea${cell?.card_name ? ` — was ${cell.card_name}` : ""}`;
        const ring = actionChosen.includes(key) || selectedCell === key || (readable && readCell === key)
          ? "2px solid var(--sea)"
          : isDemolishTarget
            ? "2px dashed var(--danger)"
            : isActionTarget
              ? "2px dashed var(--sea)"
              : null;
        const classes = ["cell", isTile || isCube ? "cell--placed" : "", clickable ? "cell--buildable" : "", isSea ? "cell--sea" : "", coastal.has(key) ? "cell--front" : ""].join(" ");
        const raised = drowned && hovered === key;
        return (
          <div
            key={key}
            data-testid={`block-${r}-${c}`}
            data-height={lv}
            data-flooded={flooded ? "true" : undefined}
            className="absolute pointer-events-none"
            style={{ left: c * S, top: r * S, width: S, height: S, zIndex: raised ? LAYER.hover : geom.z(r, c) }}
          >
            <SideFaces S={S} height={lv} step={geom.lift(1)} flooded={flooded} testid={`block-sides-${r}-${c}`} />
            <button
              type="button"
              data-testid={`cell-${r}-${c}`}
              data-height={height ?? undefined}
              data-flooded={flooded ? "true" : undefined}
              data-legal={showLegality ? String(!dimmed) : undefined}
              data-read={readable && readCell === key ? "true" : undefined}
              onClick={() => {
                if (clickable) return onSelectCell(key);
                if (drowned) setHovered(key);
                if (readable) return onReadCell(readsCard ? key : null);
                return undefined;
              }}
              onMouseEnter={() => {
                setHovered(key);
                onHoverCell?.(key);
              }}
              disabled={!clickable && !readable && !onHoverCell && !drowned}
              aria-disabled={!clickable && !readsCard}
              aria-label={`${isSea ? seaLabel : cell?.card_name || `Empty square ${r},${c}`}${isCube && hasGreenStreet(cell) ? " · Green Street" : ""}${height ? ` · height ${heightLabel(height)}` : ""}`}
              className={classes}
              style={{
                position: "absolute",
                left: -f.d,
                top: -f.d,
                width: S,
                height: S,
                borderRadius: 0,
                pointerEvents: "auto",
                cursor: clickable ? undefined : readsCard ? "help" : "default",
                background: (FACE[lv] || FACE[1]).top,
              }}
              title={isSea ? (drowned ? undefined : `${seaLabel} (permanent)`) : `${cell?.card_name || ""}${isCube && hasGreenStreet(cell) ? " · Green Street" : ""}`}
            >
              {isTile && <TileImage cardKey={cell.card_key} fading={falling} />}
              {(isCube || (showGhost && !cell?.kind)) && (
                <StreetTile cellKey={key} green={isCube && hasGreenStreet(cell)} opacity={isCube ? 1 : 0.4} />
              )}
              {isCube && !hasGreenStreet(cell) && legal && selectedCard?.key === "green_street" && (hovered === key || selectedCell === key) && (
                <StreetTile cellKey={key} green ghost opacity={0.7} />
              )}
              {isSea && <SeaSquare cellKey={key} drowned={drowned} rise={seaRise[key]} tip={hovered === key} players={players} bottom={v.oB} />}
              {showGhost && <Building cellKey={key} floors={floors + 1} shade={PLAYER_SHADES[activeColor] || FALLBACK_SHADE} mode="ghost" />}
              {isCube && (
                <Building
                  key={`${key}-${floors}-${falling ? "fall" : "b"}`}
                  cellKey={key}
                  floors={floors}
                  shade={PLAYER_SHADES[cell.owner] || FALLBACK_SHADE}
                  greenRoof={!!cell.token}
                  riseFrom={riseFrom(key, cell)}
                  mode={falling ? "fall" : null}
                />
              )}
              <FaceShadows shade={geom.shade(key)} k={geom.k} cellKey={key} />
              {!isSea && heldBack.has(key) && <HeldBackOverlay cellKey={key} inset={visInset(v)} />}
              {!isSea && nextRise.has(key) && (isTile || isCube) && <NextRiseOutline cellKey={key} inset={visInset(v, 2)} plot={isCube} />}
              {isTile && cell.sabotage_marker && <SabotageMark cellKey={key} />}
              {isDemolishTarget && !falling && <DemolishMark r={r} c={c} />}
              {wallSplash[key] != null && <WallSplash cellKey={key} delay={wallSplash[key]} />}
              {dimmed && <span data-testid={`dim-${key}`} className="absolute inset-0 pointer-events-none" style={{ background: "rgba(227,222,211,0.45)", zIndex: 11 }} />}
              {ring && <span data-testid={`cell-ring-${key}`} className="absolute pointer-events-none" style={{ ...visInset(v, 2), zIndex: 12, border: ring, borderRadius: 2 }} />}
              {!isCube && <span className="absolute inset-0 pointer-events-none" style={{ boxShadow: `inset 0 0 0 0.5px ${TOP_EDGE}`, zIndex: 13 }} />}
            </button>
          </div>
        );
      })}
      <EdgeWalls walls={walls} geom={geom} players={players} picker={edgePicker} />
      <WeatherArea ws={weather} round={round} gridRows={gridRows} gridCols={gridCols} geom={geom} shuffle={weatherShuffle} />
      <BoardMarkers geom={geom} gridRows={gridRows} gridCols={gridCols} cells={cells} players={players} cellInfo={cellInfo} illegalCell={illegalCell} walls={walls} />
      <ThiefBanner banner={thiefBanner} geom={geom} />
    </div>
  );
}
